import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Modal } from '@swyftx/aviary/atoms/Modal';
import { Body, Numeric, Utility } from '@swyftx/aviary/atoms/Typography';
import { ReviewItem } from '@swyftx/aviary/molecules/ReviewItem';

import { formatCurrency } from '@shared/utils';

import { capitalize } from '@utils/formatting';

import { DateTime } from 'luxon';
import { useMarginAccount } from 'src/lib/margin/hooks/account';
import { useMarginTransactionDetails } from 'src/lib/margin/hooks/transactions';

type Props = {
  transactionId: string;
  open?: boolean;
  onClose?: () => void;
};

const MarginBalanceAdjustmentDetailsModal: React.FC<Props> = ({ transactionId, open = false, onClose }) => {
  const [modalOpen, setModalOpen] = useState<boolean>(open);
  const { currency } = useMarginAccount();
  const { transaction } = useMarginTransactionDetails({ transactionId, type: ['ADJUSTMENT'] });
  const { t } = useTranslation('margin');

  const subTitle = useMemo(() => {
    if (!transaction) return undefined;

    return (
      <FlexLayout alignItems='center' spacing={4}>
        <Utility variant='caption'>{t('transfer.labels.id', { id: transaction.transactionId })}</Utility>
        {transaction.timestamp && (
          <Utility variant='caption'>
            •{' '}
            {t('transfer.labels.date', {
              date: DateTime.fromMillis(transaction.timestamp).toFormat('dd MMM yyyy hh:mm:ss a'),
            })}
          </Utility>
        )}
      </FlexLayout>
    );
  }, [t, transaction]);

  return (
    <Modal
      position='dynamic'
      title={t('balanceAdjustment.title')}
      className='lg:min-w-[480px] lg:max-w-[480px]'
      scrollClassName='overflow-visible'
      subTitle={subTitle}
      open={modalOpen}
      onOpenChange={(o) => {
        if (!o && onClose) {
          onClose();
          return;
        }
        setModalOpen(!!o);
      }}
      onClose={onClose}
    >
      <FlexLayout direction='column' className='px-24 pb-24' spacing={12}>
        {transaction?.type && (
          <ReviewItem title={t('balanceAdjustment.labels.type')}>
            <Body weight='bold'>{capitalize(transaction.type)}</Body>
          </ReviewItem>
        )}
        {transaction?.amount && (
          <ReviewItem title={t('balanceAdjustment.labels.amount')}>
            <Numeric weight='bold'>
              {formatCurrency(transaction.amount, currency, { hideCode: false, appendCode: true })}
            </Numeric>
          </ReviewItem>
        )}
        {transaction?.reference && (
          <>
            <ReviewItem title={t('balanceAdjustment.labels.additionalDetails')} />
            <Body>{transaction?.reference || t('balanceAdjustment.labels.balanceAdjustment')}</Body>
          </>
        )}
      </FlexLayout>
    </Modal>
  );
};

export { MarginBalanceAdjustmentDetailsModal };
